<template>
  <v-tooltip bottom>
    <template #activator="{ on, attrs }">
      <div
        class="sensor-status-indicator"
        :class="ledClass"
        :data-testid="`sensor-status-led-${ledColor}`"
        v-bind="attrs"
        v-on="on"
      />
    </template>
    <div data-testid="sensor-status-tooltip">
      <div v-if="showsInactive" data-testid="sensor-status-tooltip-message">
        {{ $t('tooltip.monitoring_point_inactive') }}
      </div>
      <template v-else>
        <div
          v-if="tooltipMessage"
          :class="{ 'font-weight-medium': isStale }"
          data-testid="sensor-status-tooltip-message"
        >
          {{ tooltipMessage }}
        </div>
        <div
          v-if="hasReceived"
          :class="{ 'sensor-status-indicator__stale': isStale }"
          data-testid="sensor-status-tooltip-last-received"
        >
          <div>{{ $t('tooltip.last_reception') }}</div>
          <div data-testid="sensor-status-tooltip-last-received-value">
            <sensor-last-reception :sensor="sensor" />
          </div>
        </div>
      </template>
    </div>
  </v-tooltip>
</template>

<i18n lang="yaml">
ja:
  tooltip:
    last_reception: '最終受信状況:'
    status_inactive: '12時間以上受信していません'
    never_received: 'まだデータを受信していません'
    monitoring_point_inactive: '現在休止中です'
en:
  tooltip:
    last_reception: 'Last received:'
    status_inactive: 'No data received for over 12 hours'
    never_received: 'Never received data'
    monitoring_point_inactive: 'Currently inactive'
</i18n>

<script>
import { getUnixTime } from 'date-fns';

import SensorLastReception from '@/components/molecules/SensorLastReception';
import Features from '@/mixins/features';

export default {
  name: 'sensor-status-indicator',
  components: {
    SensorLastReception,
  },
  mixins: [
    Features,
  ],
  props: {
    sensor: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ledColor() {
      if (this.sensor.inactivated_at != null) {
        return 'gray';
      }
      if (!this.hasReceived) {
        return 'gray';
      }
      const twelveHoursInSeconds = 12 * 60 * 60;
      const now = getUnixTime(new Date());
      const timeSinceLastEvent = now - this.sensor.last_event_request_time;
      return timeSinceLastEvent < twelveHoursInSeconds ? 'green' : 'gray';
    },
    ledClass() {
      return `sensor-status-indicator--${this.ledColor}`;
    },
    // 休止中は受信が止まっているのが正常なので、受信状況を出すと
    // 12 時間の警告と同じく事実と異なる印象を与える。一覧の「（休止中）」と同じ条件で出す。
    showsInactive() {
      return this.featureIsActive('monitoring_point') && this.sensor.status === 'inactive';
    },
    hasReceived() {
      return Boolean(this.sensor.last_event_request_time);
    },
    // 受信はあったが途絶えている状態。最終受信状況が過去の値であることを見た目でも示す。
    isStale() {
      return this.hasReceived && this.ledColor === 'gray';
    },
    tooltipMessage() {
      if (!this.hasReceived) {
        return this.$t('tooltip.never_received');
      }
      // 受信できている間は最終受信日時だけを出す。状態を言葉でも重ねると、
      // 同じことを二度言うだけで読み取れる情報が増えない。
      return this.ledColor === 'green' ? null : this.$t('tooltip.status_inactive');
    },
  },
};
</script>

<style scoped lang="sass">
.sensor-status-indicator
  width: 12px
  height: 12px
  border-radius: 50%
  display: inline-block

  &--gray
    background-color: #9e9e9e
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.3)

  &--green
    background-color: #4caf50
    box-shadow: 0 0 4px rgba(76, 175, 80, 0.6)
    animation: pulse 2s ease-in-out infinite

  &__stale
    opacity: 0.8

@keyframes pulse
  0%, 100%
    opacity: 1
    box-shadow: 0 0 4px rgba(76, 175, 80, 0.6)
  50%
    opacity: 0.7
    box-shadow: 0 0 8px rgba(76, 175, 80, 0.9)
</style>
